import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  ICreateProviderFtpConnection,
  IProviderFtpConnectionResponse,
  IUpdateProviderFtpConnection,
} from './types';

export const useGetProviderFtpConnection = (
  providerId: string,
  salesChannelId: string
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IProviderFtpConnectionResponse, DefaultError>({
    queryKey: ['ftp-conection', providerId, salesChannelId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Providers/Connection/FTP`,
        params: { providerId, salesChannelId },
      });

      return responseHandler(response, {
        groupName: 'useGetProviderFtpConnections',
        notFoundPath: `/settings/provider/${providerId}/ftp-connection/create/${salesChannelId}/keys`,
      });
    },
  });
};

export const usePostProviderFtpConnection = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostProviderFtpConnections'
  );

  return useMutation<
    IProviderFtpConnectionResponse,
    DefaultError,
    ICreateProviderFtpConnection
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.create.loading', {
          what: i18next.t('provider.ftp-what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `Providers/Connection/FTP`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `usePostProviderFtpConnections`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('provider.ftp-what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutProviderFtpConnection = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutProviderFtpConnection'
  );

  return useMutation<
    IProviderFtpConnectionResponse,
    DefaultError,
    IUpdateProviderFtpConnection
  >({
    mutationFn: async body => {
      loading(
        i18next.t('create.update.loading', {
          what: i18next.t('provider.ftp-what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: `Providers/Connection/FTP`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `usePutProviderFtpConnection`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('provider.ftp-what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteProviderFtpConnection = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useDeleteProviderFtpConnection'
  );

  return useMutation<
    boolean,
    DefaultError,
    { providerId: string; salesChannelId: string }
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.remove.loading', {
          what: i18next.t('provider.ftp-what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: `Providers/Connection/FTP`,
        body,
      });

      return responseHandler(response, {
        groupName: `useDeleteProviderFtpConnection`,
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('provider.ftp-what'),
        })
      );
    },
    onError: dismiss,
  });
};
